import React, { useEffect, useState } from 'react';
import {
  Shield,
  ShieldCheck,
  Users,
  CheckCircle2,
  XCircle,
  Save,
  RotateCcw,
  UserPlus,
  Trash2,
  Layers,
  Mail,
  Megaphone,
  Gauge,
  CreditCard,
  Key,
  Activity,
  ShieldAlert,
  Loader2,
  ChevronDown,
  ChevronRight,
  Search,
} from 'lucide-react';
import { apiClient } from '../services/api-client';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import type { OrganizationMemberDto } from '@antigravity/types';

interface UamScreen {
  id: string;
  code: string;
  name: string;
  category: string;
  route: string;
  description: string;
  sortOrder: number;
  isActive: boolean;
  permissions: {
    id: string;
    screenId: string;
    actionKey: string;
    actionName: string;
    description: string;
    sortOrder: number;
    roles: Record<'SUPER_ADMIN' | 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER', boolean>;
  }[];
}

interface RolePermRecord {
  id: string;
  role: string;
  screenId: string;
  permissionId: string;
  isGranted: boolean;
  screenName: string;
  category: string;
  route: string;
  actionName: string;
  actionKey: string;
}

const ROLES: Array<'SUPER_ADMIN' | 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER'> = [
  'SUPER_ADMIN',
  'OWNER',
  'ADMIN',
  'MEMBER',
  'VIEWER',
];

const ROLE_COLORS: Record<string, { badge: string; border: string; text: string }> = {
  SUPER_ADMIN: {
    badge: 'bg-purple-950/80 text-purple-300 border-purple-800/80',
    border: 'border-purple-500/30',
    text: 'text-purple-400',
  },
  OWNER: {
    badge: 'bg-amber-950/80 text-amber-300 border-amber-800/80',
    border: 'border-amber-500/30',
    text: 'text-amber-400',
  },
  ADMIN: {
    badge: 'bg-blue-950/80 text-blue-300 border-blue-800/80',
    border: 'border-blue-500/30',
    text: 'text-blue-400',
  },
  MEMBER: {
    badge: 'bg-emerald-950/80 text-emerald-300 border-emerald-800/80',
    border: 'border-emerald-500/30',
    text: 'text-emerald-400',
  },
  VIEWER: {
    badge: 'bg-slate-900/90 text-slate-300 border-slate-700/80',
    border: 'border-slate-500/30',
    text: 'text-slate-400',
  },
};

const SCREEN_ICONS: Record<string, React.ElementType> = {
  overview: Layers,
  webmail: Mail,
  campaigns: Megaphone,
  deliverability: Gauge,
  billing: CreditCard,
  api_keys: Key,
  diagnostics: Activity,
  uam: ShieldCheck,
  platform_admin: ShieldAlert,
};

const ROLE_METADATA: Record<
  'SUPER_ADMIN' | 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER',
  {
    title: string;
    description: string;
    postgresView: string;
    level: string;
  }
> = {
  SUPER_ADMIN: {
    title: 'Platform Super Administrator',
    description: 'Platform superuser with sovereign oversight across all multi-tenant workspaces, system diagnostics, and administrative consoles.',
    postgresView: 'uam_super_admin_permissions',
    level: 'Tier 0 — Root Sovereign',
  },
  OWNER: {
    title: 'Workspace Owner',
    description: 'Primary tenant authority with full commercial control, subscription billing, team roster administration, and campaign operations.',
    postgresView: 'uam_owner_permissions',
    level: 'Tier 1 — Workspace Sovereign',
  },
  ADMIN: {
    title: 'Workspace Administrator',
    description: 'Operational lead with campaign dispatch, deliverability configuration, API key provisioning, and team invite privileges.',
    postgresView: 'uam_admin_permissions',
    level: 'Tier 2 — Workspace Operations Lead',
  },
  MEMBER: {
    title: 'Workspace Member / Operator',
    description: 'Standard campaign author and email sender with webmail and draft editing access, without administrative or destructive permissions.',
    postgresView: 'uam_member_permissions',
    level: 'Tier 3 — Standard Workspace Operator',
  },
  VIEWER: {
    title: 'Workspace Auditor / Viewer',
    description: 'Strictly read-only auditor permissions across dashboards, campaign sequence metrics, deliverability curves, and billing counters.',
    postgresView: 'uam_viewer_permissions',
    level: 'Tier 4 — Read-Only Auditor',
  },
};

export const UamPage: React.FC = () => {
  const { currentOrg } = useAuth();
  const { refreshPermissions } = usePermissions();
  const currentOrgId = currentOrg?.id || 'a0000000-0000-0000-0000-000000000001';
  const orgName = currentOrg?.name || 'Acme Cloud Corp';

  const [activeTab, setActiveTab] = useState<'matrix' | 'roles' | 'members' | 'screens'>('roles');
  const [roleFilterView, setRoleFilterView] = useState<'ALL' | 'SUPER_ADMIN' | 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER'>('ALL');
  const [screens, setScreens] = useState<UamScreen[]>([]);
  const [rolePerms, setRolePerms] = useState<RolePermRecord[]>([]);

  const [members, setMembers] = useState<OrganizationMemberDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Expanded screen accordions in matrix view
  const [expandedScreens, setExpandedScreens] = useState<Record<string, boolean>>({
    overview: true,
    webmail: true,
    campaigns: true,
    deliverability: true,
    billing: true,
    api_keys: true,
    diagnostics: true,
    uam: true,
    platform_admin: true,
  });

  // Invite member modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'ADMIN' | 'MEMBER' | 'VIEWER'>('MEMBER');
  const [inviting, setInviting] = useState(false);

  // Search filter
  const [searchQuery, setSearchQuery] = useState('');

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [screensData, rolePermsData, membersData] = await Promise.all([
        apiClient.getUamScreens(),
        apiClient.getUamRolePermissions(),
        apiClient.listMembers(currentOrgId).catch(() => []),
      ]);

      setScreens(screensData);
      setRolePerms(rolePermsData);
      setMembers(membersData);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to load UAM data from database' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, [currentOrgId]);

  // Toggle permission in local state
  const handleTogglePermission = (screenId: string, permId: string, role: string) => {

    setScreens((prev) =>
      prev.map((s) => {
        if (s.id !== screenId) return s;
        return {
          ...s,
          permissions: s.permissions.map((p) => {
            if (p.id !== permId) return p;
            return {
              ...p,
              roles: {
                ...p.roles,
                [role as any]: !p.roles[role as keyof typeof p.roles],
              },
            };
          }),
        };
      })
    );
  };

  // Toggle single permission directly in database (Role-Specific Table View)
  const handleDirectToggle = async (permId: string, role: string, currentGranted: boolean) => {
    const newGranted = !currentGranted;

    // Optimistic UI update
    setRolePerms((prev) =>
      prev.map((rp) => (rp.permissionId === permId && rp.role === role ? { ...rp, isGranted: newGranted } : rp))
    );

    try {
      await apiClient.updateUamRolePermission(role, permId, newGranted);
      await refreshPermissions();
      setMessage({ type: 'success', text: `Saved permission for role ${role}` });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({ type: 'error', text: `Failed to update permission: ${err.message}` });
      await loadAllData();
      await refreshPermissions();
    }
  };

  // Save full matrix back to database
  const handleSaveMatrix = async () => {
    try {
      setSaving(true);
      const updates: Array<{ role: string; permissionId: string; isGranted: boolean }> = [];

      for (const s of screens) {
        for (const p of s.permissions) {
          for (const role of ROLES) {
            updates.push({
              role,
              permissionId: p.id,
              isGranted: Boolean(p.roles[role]),
            });
          }
        }
      }

      await apiClient.updateUamRolePermissionsBatch(updates);
      await refreshPermissions();
      setMessage({ type: 'success', text: `Successfully persisted ${updates.length} screen permissions to database!` });
      setTimeout(() => setMessage(null), 4000);
      await loadAllData();
    } catch (err: any) {
      setMessage({ type: 'error', text: `Failed to save matrix: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  // Batch grant all permissions for a role
  const handleBatchGrantRole = async (role: string) => {
    try {
      setSaving(true);
      const roleRecords = rolePerms.filter((rp) => rp.role === role);
      const updates = roleRecords.map((rp) => ({
        role,
        permissionId: rp.permissionId,
        isGranted: role === 'SUPER_ADMIN' ? true : rp.permissionId !== 'p_admin_portal', // Protect super-admin console for non-superadmin roles
      }));

      await apiClient.updateUamRolePermissionsBatch(updates);
      await refreshPermissions();
      setMessage({ type: 'success', text: `Granted all tenant screen capabilities for role ${role}!` });
      setTimeout(() => setMessage(null), 3000);
      await loadAllData();
    } catch (err: any) {
      setMessage({ type: 'error', text: `Failed to batch grant: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  // Batch revoke all capabilities for a role
  const handleBatchRevokeRole = async (role: string) => {
    try {
      setSaving(true);
      const roleRecords = rolePerms.filter((rp) => rp.role === role);
      const updates = roleRecords.map((rp) => ({
        role,
        permissionId: rp.permissionId,
        isGranted: false,
      }));

      await apiClient.updateUamRolePermissionsBatch(updates);
      await refreshPermissions();
      setMessage({ type: 'success', text: `Revoked all capabilities for role ${role}!` });
      setTimeout(() => setMessage(null), 3000);
      await loadAllData();
    } catch (err: any) {
      setMessage({ type: 'error', text: `Failed to batch revoke: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };


  // Role change handler for team member
  const handleRoleChange = async (memberId: string, newRole: 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER') => {
    try {
      await apiClient.updateMemberRole(currentOrgId, memberId, { role: newRole });
      setMessage({ type: 'success', text: `Role updated to ${newRole} successfully!` });
      setTimeout(() => setMessage(null), 3000);
      await loadAllData();
    } catch (err: any) {
      setMessage({ type: 'error', text: `Failed to update member role: ${err.message}` });
    }
  };

  // Invite member
  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;

    try {
      setInviting(true);
      await apiClient.inviteMember(currentOrgId, { email: inviteEmail, role: inviteRole });
      setMessage({ type: 'success', text: `Invitation sent to ${inviteEmail} with role ${inviteRole}!` });
      setShowInviteModal(false);
      setInviteEmail('');
      setTimeout(() => setMessage(null), 4000);
      await loadAllData();
    } catch (err: any) {
      setMessage({ type: 'error', text: `Failed to invite member: ${err.message}` });
    } finally {
      setInviting(false);
    }
  };

  // Remove member
  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from this workspace?`)) return;

    try {
      await apiClient.removeMember(currentOrgId, memberId);
      setMessage({ type: 'success', text: `Removed ${memberName} from workspace.` });
      setTimeout(() => setMessage(null), 3000);
      await loadAllData();
    } catch (err: any) {
      setMessage({ type: 'error', text: `Failed to remove member: ${err.message}` });
    }
  };

  const toggleAccordion = (code: string) => {
    setExpandedScreens((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  const filteredScreens = screens.filter(
    (s) =>
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.route.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.permissions.some((p) => p.actionName.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-800/80">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-white flex items-center gap-2">
                User Access Management (UAM)
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-300 font-mono border border-slate-700">
                  {orgName}
                </span>
              </h1>
              <p className="text-sm text-slate-400 mt-0.5">
                Manage team identities, assign tenant roles, and configure granular role-to-screen access boundaries.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadAllData}
            disabled={loading}
            className="px-3.5 py-2 rounded-xl text-xs font-medium bg-slate-900 border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800 transition flex items-center gap-2"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {activeTab === 'matrix' && (
            <button
              onClick={handleSaveMatrix}
              disabled={saving}
              className="px-4 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white transition flex items-center gap-2 shadow-lg shadow-brand-500/20"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save Permissions Matrix
            </button>
          )}
          {activeTab === 'members' && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white transition flex items-center gap-2 shadow-lg shadow-brand-500/20"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Invite Member
            </button>
          )}
        </div>
      </div>

      {/* Notification Toast */}
      {message && (
        <div
          className={`p-3.5 rounded-xl border text-sm flex items-center justify-between ${
            message.type === 'success'
              ? 'bg-emerald-950/60 border-emerald-800/80 text-emerald-300'
              : 'bg-rose-950/60 border-rose-800/80 text-rose-300'
          }`}
        >
          <div className="flex items-center gap-2.5">
            {message.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            ) : (
              <XCircle className="w-4 h-4 text-rose-400" />
            )}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-xs opacity-70 hover:opacity-100">
            Dismiss
          </button>
        </div>
      )}

      {/* KPI Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-400">
            <Layers className="w-4 h-4 text-blue-400" />
            <span>Registered Screens</span>
          </div>
          <div className="text-2xl font-bold text-white mt-1.5">{screens.length || 9}</div>
          <p className="text-[11px] text-slate-500 mt-1">100% seeded in database</p>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-400">
            <Shield className="w-4 h-4 text-purple-400" />
            <span>Granular Permissions</span>
          </div>
          <div className="text-2xl font-bold text-white mt-1.5">
            {screens.reduce((acc, s) => acc + s.permissions.length, 0) || 26}
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Actions across all modules</p>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-400">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>Role Permissions Mapped</span>
          </div>
          <div className="text-2xl font-bold text-white mt-1.5">{rolePerms.length || 130}</div>
          <p className="text-[11px] text-slate-500 mt-1">Across 5 platform roles</p>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-400">
            <Users className="w-4 h-4 text-amber-400" />
            <span>Active Team Members</span>
          </div>
          <div className="text-2xl font-bold text-white mt-1.5">{members.length || 4}</div>
          <p className="text-[11px] text-slate-500 mt-1">{orgName} Workspace</p>
        </div>
      </div>

      {/* Main Tabs Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 pb-2">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('matrix')}
            className={`px-4 py-2 rounded-xl text-xs font-semibold transition flex items-center gap-2 ${
              activeTab === 'matrix'
                ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            Full Screen Permission Matrix
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`px-4 py-2 rounded-xl text-xs font-semibold transition flex items-center gap-2 ${
              activeTab === 'roles'
                ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <Shield className="w-4 h-4" />
            Dedicated Role Tables
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 rounded-xl text-xs font-semibold transition flex items-center gap-2 ${
              activeTab === 'members'
                ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <Users className="w-4 h-4" />
            Team Members & Roles ({members.length})
          </button>
          <button
            onClick={() => setActiveTab('screens')}
            className={`px-4 py-2 rounded-xl text-xs font-semibold transition flex items-center gap-2 ${
              activeTab === 'screens'
                ? 'bg-brand-600/20 text-brand-300 border border-brand-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <Layers className="w-4 h-4" />
            Screen Registry Database ({screens.length})
          </button>
        </div>

        {/* Search */}
        {(activeTab === 'matrix' || activeTab === 'roles' || activeTab === 'screens') && (
          <div className="relative w-64">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Search screens, actions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-brand-500"
            />
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center text-slate-400 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-brand-500" />
          <p className="text-sm font-medium">Loading UAM Relational Data from Database...</p>
        </div>
      ) : (
        <>
          {/* ========================================================================= */}
          {/* TAB 1: FULL SCREEN PERMISSION MATRIX (MASTER GRID) */}
          {/* ========================================================================= */}
          {activeTab === 'matrix' && (
            <div className="space-y-4">
              <div className="bg-slate-900/40 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-900/80 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                        <th className="py-3.5 px-4 w-2/5">Screen / Capability</th>
                        <th className="py-3.5 px-3 text-center w-28">
                          <span className="px-2 py-0.5 rounded-md bg-purple-950/80 text-purple-300 border border-purple-800/80">
                            SUPER_ADMIN
                          </span>
                        </th>
                        <th className="py-3.5 px-3 text-center w-24">
                          <span className="px-2 py-0.5 rounded-md bg-amber-950/80 text-amber-300 border border-amber-800/80">
                            OWNER
                          </span>
                        </th>
                        <th className="py-3.5 px-3 text-center w-24">
                          <span className="px-2 py-0.5 rounded-md bg-blue-950/80 text-blue-300 border border-blue-800/80">
                            ADMIN
                          </span>
                        </th>
                        <th className="py-3.5 px-3 text-center w-24">
                          <span className="px-2 py-0.5 rounded-md bg-emerald-950/80 text-emerald-300 border border-emerald-800/80">
                            MEMBER
                          </span>
                        </th>
                        <th className="py-3.5 px-3 text-center w-24">
                          <span className="px-2 py-0.5 rounded-md bg-slate-800 text-slate-300 border border-slate-700">
                            VIEWER
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/60 text-xs">
                      {filteredScreens.map((screen) => {
                        const IconComponent = SCREEN_ICONS[screen.code] || Layers;
                        const isExpanded = expandedScreens[screen.code];

                        return (
                          <React.Fragment key={screen.id}>
                            {/* Screen Header Row */}
                            <tr className="bg-slate-900/60 hover:bg-slate-800/40 transition-colors">
                              <td colSpan={6} className="py-2.5 px-4">
                                <div
                                  onClick={() => toggleAccordion(screen.code)}
                                  className="flex items-center justify-between cursor-pointer select-none group"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-7 h-7 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300 group-hover:text-white transition">
                                      <IconComponent className="w-4 h-4" />
                                    </div>
                                    <div>
                                      <div className="font-semibold text-white flex items-center gap-2">
                                        <span>{screen.name}</span>
                                        <span className="text-[10px] text-slate-400 font-mono bg-slate-800/80 px-2 py-0.5 rounded border border-slate-700/60">
                                          {screen.route}
                                        </span>
                                        <span className="text-[10px] text-brand-400 font-semibold bg-brand-950/40 px-2 py-0.5 rounded border border-brand-900/40">
                                          {screen.category}
                                        </span>
                                      </div>
                                      <p className="text-[11px] text-slate-400 mt-0.5">{screen.description}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 text-slate-400">
                                    <span className="text-[11px] text-slate-400">{screen.permissions.length} actions</span>
                                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                  </div>
                                </div>
                              </td>
                            </tr>

                            {/* Sub-Actions Rows */}
                            {isExpanded &&
                              screen.permissions.map((perm) => (
                                <tr key={perm.id} className="hover:bg-slate-800/20 transition-colors bg-slate-950/30">
                                  <td className="py-2.5 pl-12 pr-4">
                                    <div className="font-medium text-slate-200">{perm.actionName}</div>
                                    <div className="text-[11px] text-slate-400 mt-0.5">{perm.description}</div>
                                    <div className="text-[10px] font-mono text-slate-400 mt-0.5">{perm.actionKey}</div>
                                  </td>
                                  {ROLES.map((role) => {
                                    const isGranted = Boolean(perm.roles[role]);

                                    return (
                                      <td key={role} className="py-2.5 px-3 text-center">
                                        <button
                                          type="button"
                                          onClick={() => handleTogglePermission(screen.id, perm.id, role)}
                                          className={`w-7 h-7 rounded-lg inline-flex items-center justify-center transition cursor-pointer ${
                                            isGranted
                                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/30'
                                              : 'bg-slate-800/40 text-slate-600 border border-slate-700/50 hover:bg-slate-800 hover:text-slate-400'
                                          }`}
                                          title={`${role}: ${isGranted ? 'Granted' : 'Denied'}`}
                                        >
                                          {isGranted ? (
                                            <CheckCircle2 className="w-4 h-4" />
                                          ) : (
                                            <XCircle className="w-4 h-4" />
                                          )}
                                        </button>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* TAB 2: DEDICATED ROLE TABLES (THANI THANI TABLES FOR EVERY ROLE) */}
          {/* ========================================================================= */}
          {activeTab === 'roles' && (
            <div className="space-y-6">
              {/* Role Navigation Bar & Filter Controls */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-900/60 p-3 rounded-2xl border border-slate-800">
                <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
                  <button
                    onClick={() => setRoleFilterView('ALL')}
                    className={`px-3.5 py-2 rounded-xl text-xs font-bold transition border flex items-center gap-2 ${
                      roleFilterView === 'ALL'
                        ? 'bg-brand-600 text-white border-brand-500 shadow-md shadow-brand-500/20'
                        : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    <Layers className="w-3.5 h-3.5" />
                    <span>All 5 Role Tables (Thani Thani Tables)</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded bg-black/40 font-mono">5</span>
                  </button>

                  <div className="h-4 w-px bg-slate-800 mx-1 hidden sm:block" />

                  {ROLES.map((role) => (
                    <button
                      key={role}
                      onClick={() => setRoleFilterView(role)}
                      className={`px-3 py-2 rounded-xl text-xs font-semibold transition border flex items-center gap-2 ${
                        roleFilterView === role
                          ? ROLE_COLORS[role].badge
                          : 'bg-slate-900/70 border-slate-800 text-slate-400 hover:text-white'
                      }`}
                    >
                      <span>{role}</span>
                      <span className="text-[10px] px-1.5 py-0.2 rounded bg-black/30 font-mono">
                        {rolePerms.filter((rp) => rp.role === role && rp.isGranted).length} /{' '}
                        {rolePerms.filter((rp) => rp.role === role).length || 26}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="text-xs text-slate-400 font-medium">
                  {roleFilterView === 'ALL'
                    ? 'Displaying all 5 role tables sequentially'
                    : `Displaying dedicated table for role: ${roleFilterView}`}
                </div>
              </div>

              {/* Render either All 5 Tables or the Selected Role's Table */}
              {(roleFilterView === 'ALL' ? ROLES : [roleFilterView]).map((role) => {
                const meta = ROLE_METADATA[role];
                const currentRolePerms = rolePerms
                  .filter((rp) => rp.role === role)
                  .filter(
                    (rp) =>
                      !searchQuery ||
                      rp.screenName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      rp.actionName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      rp.actionKey.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      rp.category.toLowerCase().includes(searchQuery.toLowerCase())
                  );

                const totalForRole = rolePerms.filter((rp) => rp.role === role).length || 26;
                const grantedForRole = rolePerms.filter((rp) => rp.role === role && rp.isGranted).length;
                const percent = Math.round((grantedForRole / totalForRole) * 100);

                return (
                  <div
                    key={role}
                    className="bg-slate-900/40 border border-slate-800 rounded-2xl overflow-hidden shadow-xl"
                  >
                    {/* Role Table Header Banner */}
                    <div className="p-4 bg-slate-900/80 border-b border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex flex-wrap items-center gap-2.5">
                          <span
                            className={`text-xs px-2.5 py-0.5 rounded-md font-bold tracking-wide border ${ROLE_COLORS[role].badge}`}
                          >
                            {role}
                          </span>
                          <h2 className="text-sm font-bold text-white">{meta.title}</h2>
                          <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-slate-800/80 text-brand-300 border border-slate-700/80">
                            PostgreSQL View: {meta.postgresView}
                          </span>
                          <span className="text-[10px] text-slate-500 font-mono">({meta.level})</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1 max-w-3xl">{meta.description}</p>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-xs font-bold text-white flex items-center justify-end gap-1.5">
                            <span className={percent === 100 ? 'text-purple-400' : percent > 50 ? 'text-emerald-400' : 'text-amber-400'}>
                              {grantedForRole} / {totalForRole} Granted
                            </span>
                            <span className="text-[11px] text-slate-500 font-mono">({percent}%)</span>
                          </div>
                          {/* Progress bar */}
                          <div className="w-32 h-1.5 bg-slate-800 rounded-full mt-1.5 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-300 ${
                                role === 'SUPER_ADMIN'
                                  ? 'bg-purple-500'
                                  : role === 'OWNER'
                                  ? 'bg-amber-500'
                                  : role === 'ADMIN'
                                  ? 'bg-blue-500'
                                  : role === 'MEMBER'
                                  ? 'bg-emerald-500'
                                  : 'bg-slate-500'
                              }`}
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleBatchGrantRole(role)}
                            disabled={saving}
                            className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-emerald-950/60 text-emerald-300 border border-emerald-800/60 hover:bg-emerald-900/60 transition"
                            title="Grant all capabilities for this role"
                          >
                            Grant All
                          </button>
                          <button
                            type="button"
                            onClick={() => handleBatchRevokeRole(role)}
                            disabled={saving}
                            className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-rose-950/60 text-rose-300 border border-rose-800/60 hover:bg-rose-900/60 transition"
                            title="Revoke all capabilities for this role"
                          >
                            Revoke All
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Dedicated Table Body */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead>
                          <tr className="border-b border-slate-800 bg-slate-900/60 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                            <th className="py-3 px-4 w-1/4">Screen / Module</th>
                            <th className="py-3 px-4 w-1/4">Capability Action</th>
                            <th className="py-3 px-4 w-32">Route</th>
                            <th className="py-3 px-4 w-40 font-mono">Action Key</th>
                            <th className="py-3 px-4 text-center w-28">Status</th>
                            <th className="py-3 px-4 text-right w-40">Live Action Toggle</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/60">
                          {currentRolePerms.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="py-6 text-center text-slate-500 italic">
                                No permissions matched the search filter for role {role}.
                              </td>
                            </tr>
                          ) : (
                            currentRolePerms.map((rp) => {
                              const ScreenIcon = SCREEN_ICONS[rp.screenId.replace('scr_', '')] || Layers;

                              return (
                                <tr key={`${role}-${rp.permissionId}`} className="hover:bg-slate-800/30 transition-colors">
                                  <td className="py-3 px-4 font-semibold text-white">
                                    <div className="flex items-center gap-2.5">
                                      <div className="w-6 h-6 rounded-md bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400">
                                        <ScreenIcon className="w-3.5 h-3.5" />
                                      </div>
                                      <div>
                                        <div className="text-white font-medium">{rp.screenName}</div>
                                        <span className="text-[10px] text-slate-400 font-normal">
                                          {rp.category}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4">
                                    <div className="font-semibold text-slate-200">{rp.actionName}</div>
                                    <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">
                                      {screens
                                        .find((s) => s.id === rp.screenId)
                                        ?.permissions.find((p) => p.id === rp.permissionId)?.description || ''}
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 font-mono text-[11px] text-slate-300">
                                    <span className="px-2 py-0.5 rounded bg-slate-800/80 text-slate-300 border border-slate-700/60">
                                      {rp.route}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 font-mono text-[11px] text-slate-500">{rp.actionKey}</td>
                                  <td className="py-3 px-4 text-center">
                                    {rp.isGranted ? (
                                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-950/80 text-emerald-400 border border-emerald-800/80 shadow-sm shadow-emerald-950">
                                        <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                                        GRANTED
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-rose-950/80 text-rose-400 border border-rose-800/80 shadow-sm shadow-rose-950">
                                        <XCircle className="w-3 h-3 text-rose-400" />
                                        DENIED
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4 text-right">
                                    <button
                                      type="button"
                                      onClick={() => handleDirectToggle(rp.permissionId, rp.role, rp.isGranted)}
                                      className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition flex items-center justify-center gap-1.5 ml-auto ${
                                        rp.isGranted
                                          ? 'bg-rose-500/15 text-rose-300 border border-rose-500/30 hover:bg-rose-500/25 active:scale-95'
                                          : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 active:scale-95'
                                      }`}
                                    >
                                      {rp.isGranted ? (
                                        <>
                                          <XCircle className="w-3.5 h-3.5 text-rose-400" />
                                          <span>Revoke Access</span>
                                        </>
                                      ) : (
                                        <>
                                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                          <span>Grant Access</span>
                                        </>
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}


          {/* ========================================================================= */}
          {/* TAB 3: TEAM MEMBERS & ROLE GOVERNANCE */}
          {/* ========================================================================= */}
          {activeTab === 'members' && (
            <div className="space-y-4">
              <div className="bg-slate-900/40 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                <div className="p-4 bg-slate-900/80 border-b border-slate-800 flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-bold text-white flex items-center gap-2">
                      <span>Team Member Access Roster</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                        {members.length} Members
                      </span>
                    </h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Assign roles (`OWNER`, `ADMIN`, `MEMBER`, `VIEWER`) to immediately update user authority across the platform.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white transition flex items-center gap-1.5"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    Invite User
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-900/60 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                        <th className="py-3 px-4">Member Name</th>
                        <th className="py-3 px-4">Email Address</th>
                        <th className="py-3 px-4">Current Role</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4">Joined Date</th>
                        <th className="py-3 px-4 text-right">Role Assignment</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/60">
                      {members.map((member) => {
                        const fullName = `${member.user?.firstName || 'User'} ${member.user?.lastName || ''}`.trim();
                        const isOwner = member.role === 'OWNER';

                        return (
                          <tr key={member.id} className="hover:bg-slate-800/30 transition-colors">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center font-bold text-xs text-brand-300">
                                  {member.user?.firstName?.[0] || 'U'}
                                  {member.user?.lastName?.[0] || ''}
                                </div>
                                <div className="font-semibold text-white">{fullName}</div>
                              </div>
                            </td>
                            <td className="py-3 px-4 font-mono text-slate-300">{member.user?.email}</td>
                            <td className="py-3 px-4">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-semibold border ${
                                  ROLE_COLORS[member.role]?.badge || 'bg-slate-800 text-slate-300'
                                }`}
                              >
                                {member.role}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                  member.status === 'ACTIVE'
                                    ? 'bg-emerald-950/80 text-emerald-400 border border-emerald-800/80'
                                    : 'bg-amber-950/80 text-amber-400 border border-amber-800/80'
                                }`}
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                {member.status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-slate-400">
                              {new Date(member.createdAt).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <select
                                value={member.role}
                                onChange={(e) => handleRoleChange(member.id, e.target.value as any)}
                                className="bg-slate-900 border border-slate-700 text-slate-200 text-xs rounded-lg px-2.5 py-1 focus:outline-none focus:border-brand-500 font-medium cursor-pointer"
                              >
                                <option value="OWNER">OWNER</option>
                                <option value="ADMIN">ADMIN</option>
                                <option value="MEMBER">MEMBER</option>
                                <option value="VIEWER">VIEWER</option>
                              </select>
                            </td>
                            <td className="py-3 px-4 text-right">
                              {!isOwner ? (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveMember(member.id, fullName)}
                                  className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-950/40 rounded-lg transition"
                                  title="Remove Member"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              ) : (
                                <span className="text-[11px] text-slate-600 italic">Workspace Owner</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* TAB 4: DATABASE SCREEN REGISTRY TABLE */}
          {/* ========================================================================= */}
          {activeTab === 'screens' && (
            <div className="space-y-4">
              <div className="bg-slate-900/40 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                <div className="p-4 bg-slate-900/80 border-b border-slate-800 flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-bold text-white flex items-center gap-2">
                      <span>PostgreSQL Screen Registry (`uam_screens`)</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-brand-950 text-brand-300 border border-brand-800 font-mono">
                        9 Screens Registered
                      </span>
                    </h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Direct view of the 9 core application screens persisted in the database table with route definitions and action counts.
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-900/60 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                        <th className="py-3 px-4">Screen Code</th>
                        <th className="py-3 px-4">Display Name</th>
                        <th className="py-3 px-4">Category</th>
                        <th className="py-3 px-4">Route Path</th>
                        <th className="py-3 px-4">Actions Count</th>
                        <th className="py-3 px-4">Description</th>
                        <th className="py-3 px-4 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/60">
                      {filteredScreens.map((s) => (
                        <tr key={s.id} className="hover:bg-slate-800/30 transition-colors">
                          <td className="py-3 px-4 font-mono font-semibold text-brand-400">{s.code}</td>
                          <td className="py-3 px-4 font-semibold text-white">{s.name}</td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700 text-[11px]">
                              {s.category}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-mono text-[11px] text-slate-300">{s.route}</td>
                          <td className="py-3 px-4 font-mono text-[11px] text-emerald-400 font-bold">
                            {s.permissions.length} actions
                          </td>
                          <td className="py-3 px-4 text-slate-400 max-w-xs truncate">{s.description}</td>
                          <td className="py-3 px-4 text-center">
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-950/80 text-emerald-400 border border-emerald-800/80">
                              ACTIVE
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-brand-400" />
                Invite Team Member
              </h3>
              <button onClick={() => setShowInviteModal(false)} className="text-slate-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">User Email Address</label>
                <input
                  type="email"
                  required
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white focus:outline-none focus:border-brand-500 placeholder-slate-600"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">Assign Initial Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as any)}
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white focus:outline-none focus:border-brand-500 cursor-pointer"
                >
                  <option value="ADMIN">ADMIN — Full configuration & campaigns</option>
                  <option value="MEMBER">MEMBER — Author campaigns & send emails</option>
                  <option value="VIEWER">VIEWER — Read-only observation & audit</option>
                </select>
              </div>

              <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800 text-xs text-slate-400">
                The user will be granted permissions matching the screen matrix configured for the selected role.
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white flex items-center gap-2"
                >
                  {inviting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
